/**
 * zipCentral.js — потоковое чтение ZIP через центральный каталог. [10.19.39]
 *
 * Родился из улики владельца: на десктопе импорт ZIP падал с «Failed to
 * execute 'postMessage' on 'Worker': Data cannot be cloned, out of memory».
 * Прежний путь читал ВЕСЬ архив в память и отдавал fflate.unzip, а тот
 * внутри поднимает собственные воркеры и постит им данные — на
 * многогигабайтном архиве клонирование рвётся. Закон уточнён: дело не в
 * «чужом» архиваторе, а в том, СКОЛЬКО памяти просит путь.
 *
 * Здесь путь просит почти ничего:
 *   • оглавление (EOCD → центральный каталог) — килобайты с хвоста файла;
 *   • запись без сжатия (метод 0) — ЛОМТИК исходного Blob: диск, не RAM;
 *   • запись deflate (метод 8) — распаковка ОДНОЙ записи синхронным
 *     inflate: миллисекунды на срез, без воркеров и без клонов.
 *
 * Центральный каталог — а не проход по локальным заголовкам — потому, что
 * только он честен при data-descriptor (бит 3): размеры там всегда есть.
 * zip64 поддержан для каталога (локатор + EOCD64 + extra 0x0001).
 *
 * [10.19.41] ИСТОЧНИК ДВУХРЕЖИМНЫЙ: Blob (потоково, ломтиками с диска) ИЛИ
 * Uint8Array (весь архив уже в памяти — телефонный путь). Регресс владельца
 * это потребовал: на Android КАЖДОЕ обращение к поставщику платное, и
 * потоковый путь превратил одно чтение архива в 1240 (620 локальных
 * заголовков + 620 slice-чтений по 81.4 мс — вчетверо дороже файла из
 * папки). В буферном режиме store-записи выходят ПОДМАССИВАМИ без копий.
 *
 * Модуль ЧИСТЫЙ: fflate не импортирует — функцию inflate ему подают
 * (в браузере — fflate.inflateSync, в тестах — node:zlib.inflateRawSync).
 */

const СИГ_EOCD = 0x06054b50;
const СИГ_EOCD64_ЛОКАТОР = 0x07064b50;
const СИГ_EOCD64 = 0x06064b50;
const СИГ_ЦК = 0x02014b50;
const СИГ_ЛОК = 0x04034b50;

// Абстракция источника: у Blob — асинхронные ломтики, у буфера — виды.
const _ист = (и) => и instanceof Uint8Array
  ? { размер: и.length,
      кусок: async (а, б) => new DataView(и.buffer, и.byteOffset + а, б - а),
      вид: (а, б) => и.subarray(а, б) }
  : { размер: и.size,
      кусок: async (а, б) => new DataView(await и.slice(а, б).arrayBuffer()),
      вид: null };

const u16 = (в, о) => в.getUint16(о, true);
const u32 = (в, о) => в.getUint32(о, true);
const u64 = (в, о) => Number(в.getBigUint64(о, true));

export class ЗаписьНеПоддержана extends Error {
  constructor(имя, метод, шифрована) {
    super(шифрована
      ? `запись «${имя}» зашифрована — этот путь читает только открытые архивы`
      : `запись «${имя}» сжата методом ${метод} — поддержаны только store (0) и deflate (8)`);
    this.имя = имя; this.метод = метод; this.шифрована = !!шифрована;
  }
}

/**
 * Прочитать оглавление архива, не поднимая его в память.
 * @param {Blob} blob
 * @returns {Promise<{записи: Array<{имя:string, метод:number, сжато:number,
 *   размер:number, лок:number, шифрована:boolean}>, zip64: boolean}>}
 */
export async function читатьОглавление(источник) {
  const ист = _ист(источник);
  const размер = ист.размер;
  // EOCD: 22 байта + комментарий до 65535 — читаем хвост одним ломтиком.
  const хвостДлина = Math.min(размер, 22 + 65535);
  const хвост = await ист.кусок(размер - хвостДлина, размер);
  let eocd = -1;
  for (let о = хвост.byteLength - 22; о >= 0; о--) {
    if (u32(хвост, о) === СИГ_EOCD) { eocd = о; break; }
  }
  if (eocd < 0) throw new Error('не найден конец центрального каталога (EOCD) — это не ZIP или он повреждён');

  let всего = u16(хвост, eocd + 10);
  let цкРазмер = u32(хвост, eocd + 12);
  let цкСмещение = u32(хвост, eocd + 16);
  let zip64 = false;

  if (всего === 0xffff || цкРазмер === 0xffffffff || цкСмещение === 0xffffffff) {
    // zip64: локатор лежит РОВНО перед EOCD (20 байт).
    const локО = eocd - 20;
    if (локО >= 0 && u32(хвост, локО) === СИГ_EOCD64_ЛОКАТОР) {
      zip64 = true;
      const eocd64Смещ = u64(хвост, локО + 8);
      const е64 = await ист.кусок(eocd64Смещ, eocd64Смещ + 56);
      if (u32(е64, 0) !== СИГ_EOCD64) throw new Error('zip64: повреждён EOCD64');
      всего = u64(е64, 32);
      цкРазмер = u64(е64, 40);
      цкСмещение = u64(е64, 48);
    }
  }

  // [10.19.40] СДВИГ ПРЕФИКСА — классика взрослых читалок. Смещения в
  // каталоге считаются ОТ НАЧАЛА АРХИВА, а не файла; у самораспаковывающихся,
  // докачанных и пересобранных архивов перед архивом лежит чужой префикс.
  // Фактическую позицию EOCD мы знаем точно (нашли сигнатуру в файле):
  // сдвиг = позиция_EOCD − (цкСмещение + цкРазмер). Воспроизведено фантомом
  // prefixed.zip: без коррекции — «каталог повреждён на записи 0», и у
  // владельца это уводило импорт в запасной путь с воркерами fflate.
  const eocdАбс = (размер - хвостДлина) + eocd;
  let сдвиг = 0;
  if (!zip64) {
    сдвиг = eocdАбс - (цкСмещение + цкРазмер);
    if (сдвиг > 0) цкСмещение += сдвиг;
    if (сдвиг < 0) сдвиг = 0; // отрицательный сдвиг — не префикс, а порча
  }
  const цк = await ист.кусок(цкСмещение, цкСмещение + цкРазмер);
  const дек = new TextDecoder('utf-8');
  const записи = [];
  let о = 0;
  for (let i = 0; i < всего && о + 46 <= цк.byteLength; i++) {
    if (u32(цк, о) !== СИГ_ЦК) throw new Error(`центральный каталог повреждён на записи ${i}`);
    const флаги = u16(цк, о + 8);
    const метод = u16(цк, о + 10);
    let сжато = u32(цк, о + 20);
    let размерЗ = u32(цк, о + 24);
    const имяДл = u16(цк, о + 28);
    const экстраДл = u16(цк, о + 30);
    const комДл = u16(цк, о + 32);
    let лок = u32(цк, о + 42);
    const имя = дек.decode(new Uint8Array(цк.buffer, цк.byteOffset + о + 46, имяДл));

    // zip64 extra (id 0x0001): поля идут В ПОРЯДКЕ помеченных 0xFFFFFFFF.
    if (сжато === 0xffffffff || размерЗ === 0xffffffff || лок === 0xffffffff) {
      let э = о + 46 + имяДл;
      const конец = э + экстраДл;
      while (э + 4 <= конец) {
        const ид = u16(цк, э), дл = u16(цк, э + 2);
        if (ид === 0x0001) {
          let п = э + 4;
          if (размерЗ === 0xffffffff) { размерЗ = u64(цк, п); п += 8; }
          if (сжато === 0xffffffff) { сжато = u64(цк, п); п += 8; }
          if (лок === 0xffffffff) { лок = u64(цк, п); }
          break;
        }
        э += 4 + дл;
      }
    }

    if (!имя.endsWith('/')) {
      записи.push({ имя, метод, сжато, размер: размерЗ, лок: лок + сдвиг, шифрована: (флаги & 0x0001) !== 0 });
    }
    о += 46 + имяДл + экстраДл + комДл;
  }
  return { записи, zip64 };
}

/**
 * Начало данных записи: локальный заголовок несёт СВОИ длины имени и extra
 * (они могут отличаться от каталога) — читаем ровно 30 байт заголовка.
 */
/**
 * [10.19.40] Похоже ли имя записи на DICOM-файл. Чужие архиваторы не знают
 * нашей раскладки dicom/…, а настоящие DICOM бывают без расширения и даже
 * без DICM-преамбулы — поэтому здесь ЧЁРНЫЙ список мусора, а не белый:
 * всё, что не мусор, идёт в конвейер, и уже он честно отсеет неразбираемое
 * (сверка покажет числа). study.json/annotations.json ловятся раньше по именам.
 */
const _МУСОР = new Set(['json','txt','xml','html','htm','png','jpg','jpeg','gif','bmp','pdf','csv','ini','log','md','exe','dll','db','zip','7z','rar','doc','docx','xls','xlsx']);
export function похожеНаДиком(имя) {
  const база = имя.split('/').pop();
  if (!база) return false;
  if (имя.startsWith('dicom/')) return true;
  const м = база.match(/\.([a-z0-9]{1,5})$/i);
  if (!м) return true;                      // без расширения — типичный DICOM
  const рш = м[1].toLowerCase();
  if (рш === 'dcm' || рш === 'ima' || рш === 'dicom') return true;
  return !_МУСОР.has(рш) ? true : false;
}

export async function смещениеДанных(источник, запись) {
  const ш = await _ист(источник).кусок(запись.лок, запись.лок + 30);
  if (u32(ш, 0) !== СИГ_ЛОК) throw new Error(`запись «${запись.имя}»: локальный заголовок не найден`);
  return запись.лок + 30 + u16(ш, 26) + u16(ш, 28);
}

/**
 * Материализовать одну запись.
 *  метод 0 → Blob-ломтик ИСХОДНОГО файла (диск, не RAM);
 *  метод 8 → Uint8Array через поданный inflateRaw (одна запись за раз).
 * @param {Blob} blob
 * @param {object} запись — из читатьОглавление
 * @param {(сжатое: Uint8Array) => Uint8Array} inflateRaw — напр. fflate.inflateSync
 * @returns {Promise<Blob|Uint8Array>}
 */
export async function материализовать(источник, запись, inflateRaw) {
  if (запись.шифрована) throw new ЗаписьНеПоддержана(запись.имя, запись.метод, true);
  const ист = _ист(источник);
  const старт = await смещениеДанных(источник, запись);
  if (запись.метод === 0) {
    // Буфер → подмассив БЕЗ копии (view.buffer тот же); Blob → дисковый ломтик.
    return ист.вид ? ист.вид(старт, старт + запись.сжато) : источник.slice(старт, старт + запись.сжато);
  }
  if (запись.метод === 8) {
    const сжатое = ист.вид ? ист.вид(старт, старт + запись.сжато)
      : new Uint8Array(await источник.slice(старт, старт + запись.сжато).arrayBuffer());
    return inflateRaw(сжатое);
  }
  throw new ЗаписьНеПоддержана(запись.имя, запись.метод, false);
}
